import { useCallback, useEffect, useRef } from 'react';
import { CanvasPresenter, type BrushOptions, type StrokePoint } from '@design-ai/gpu-core';
import type { Engine } from '../engine.js';

export type Tool = 'pan' | 'erase' | 'restore';

export interface ViewportProps {
  engine: Engine;
  tool: Tool;
  brush: BrushOptions;
  showAlpha: boolean;
  /** Bumped by the app whenever the document changed and needs a redraw. */
  version: number;
  onPainted: () => void;
}

interface View {
  zoom: number;
  panX: number;
  panY: number;
}

/**
 * The canvas: WebGPU presentation plus pan, zoom and brush input.
 *
 * Strokes are batched to animation frames rather than applied per pointer event.
 * Each application re-runs the whole matte post-processing chain, and a fast drag
 * produces far more pointer samples than frames - batching keeps the brush
 * responsive on a 4K crop.
 */
export function Viewport({ engine, tool, brush, showAlpha, version, onPainted }: ViewportProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const presenterRef = useRef<CanvasPresenter | null>(null);
  const viewRef = useRef<View>({ zoom: 1, panX: 0, panY: 0 });
  const strokeRef = useRef<StrokePoint[]>([]);
  const paintingRef = useRef(false);
  const panRef = useRef<{ x: number; y: number } | null>(null);
  const frameRef = useRef<number | null>(null);
  const propsRef = useRef({ tool, brush, showAlpha, onPainted });
  propsRef.current = { tool, brush, showAlpha, onPainted };

  const draw = useCallback(() => {
    const presenter = presenterRef.current;
    const image = engine.workbench.displayImage;
    if (!presenter || !image) return;
    presenter.present(image, viewRef.current, { alphaOnly: propsRef.current.showAlpha });
  }, [engine]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    presenterRef.current = new CanvasPresenter(engine.ctx, canvas);

    const observer = new ResizeObserver(() => {
      const rect = canvas.getBoundingClientRect();
      presenterRef.current?.resize(rect.width, rect.height);
      draw();
    });
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [engine, draw]);

  useEffect(() => {
    draw();
  }, [draw, version, showAlpha]);

  /** Canvas-space coordinates in backing-store pixels. */
  const toCanvasPixels = (event: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = event.currentTarget;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  const toImagePoint = (event: React.PointerEvent<HTMLCanvasElement>): StrokePoint | null => {
    const presenter = presenterRef.current;
    const image = engine.workbench.displayImage;
    if (!presenter || !image) return null;
    const canvasPoint = toCanvasPixels(event);
    return presenter.canvasToImage(image, viewRef.current, canvasPoint.x, canvasPoint.y);
  };

  const flushStroke = useCallback(() => {
    frameRef.current = null;
    const points = strokeRef.current;
    if (points.length === 0) return;
    const { tool: activeTool, brush: activeBrush, onPainted: painted } = propsRef.current;
    if (activeTool === 'pan') return;

    engine.workbench.paint(points, activeTool === 'erase' ? 'erase' : 'restore', activeBrush);
    // Keep the last sample so the next batch joins up with this one.
    strokeRef.current = points.slice(-1);
    draw();
    painted();
  }, [engine, draw]);

  const scheduleFlush = useCallback(() => {
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(flushStroke);
  }, [flushStroke]);

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const panning = tool === 'pan' || event.button === 1 || event.shiftKey;
    if (panning) {
      panRef.current = { x: event.clientX, y: event.clientY };
      return;
    }
    if (!engine.workbench.cutout) return;
    const point = toImagePoint(event);
    if (!point) return;
    paintingRef.current = true;
    strokeRef.current = [point];
    scheduleFlush();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (panRef.current) {
      const canvas = event.currentTarget;
      const rect = canvas.getBoundingClientRect();
      const view = viewRef.current;
      // Pan in normalised artwork units so it stays consistent at any zoom.
      view.panX -= ((event.clientX - panRef.current.x) / rect.width) / view.zoom;
      view.panY -= ((event.clientY - panRef.current.y) / rect.height) / view.zoom;
      panRef.current = { x: event.clientX, y: event.clientY };
      draw();
      return;
    }
    if (!paintingRef.current) return;
    const point = toImagePoint(event);
    if (!point) return;
    strokeRef.current.push(point);
    scheduleFlush();
  };

  const endPointer = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    panRef.current = null;
    if (paintingRef.current) {
      paintingRef.current = false;
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
      flushStroke();
      strokeRef.current = [];
    }
  };

  const onWheel = (event: React.WheelEvent<HTMLCanvasElement>): void => {
    const view = viewRef.current;
    const factor = Math.exp(-event.deltaY * 0.0015);
    const next = Math.min(32, Math.max(0.1, view.zoom * factor));

    // Zoom about the cursor: keep the artwork point under the pointer fixed.
    const rect = event.currentTarget.getBoundingClientRect();
    const cx = (event.clientX - rect.left) / rect.width;
    const cy = (event.clientY - rect.top) / rect.height;
    view.panX += cx * (1 / view.zoom - 1 / next);
    view.panY += cy * (1 / view.zoom - 1 / next);
    view.zoom = next;
    draw();
  };

  const resetView = (): void => {
    viewRef.current = { zoom: 1, panX: 0, panY: 0 };
    draw();
  };

  return (
    <div className="viewport">
      <canvas
        ref={canvasRef}
        className={`viewport__canvas viewport__canvas--${tool}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onWheel={onWheel}
      />
      <button type="button" className="viewport__reset" onClick={resetView}>
        Reset view
      </button>
    </div>
  );
}
